import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Card, Empty, Input, Space, Spin, Switch, Table, Tag, Typography } from 'antd';
import { CheckOutlined, CloseOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { getOrganizationManagementSpecialities, putOrganizationManagementSpeciality } from '../store/user/thunks';

const { Text, Title } = Typography;

interface OrganizationSpecialityManagementProps {
    organizationId: string;
    organizationName: string;
    maxSpecialities?: number | null;
    getOrganizationManagementSpecialities?: any;
    putOrganizationManagementSpeciality?: any;
}

interface OrganizationSpecialityRow {
    organizationSpecialityId?: string | null;
    specialityId: string;
    name: string;
    specialityStatus?: string | null;
    isAssociated: boolean;
    triageSigns: boolean;
    status: string;
}

const valueOf = (source: any, ...keys: string[]) => {
    if (!source) return undefined;
    for (const key of keys) {
        if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return undefined;
};

const normalizeRows = (source: any): OrganizationSpecialityRow[] => {
    const values = Array.isArray(source)
        ? source
        : valueOf(source, 'items', 'Items', 'data', 'Data', 'specialities', 'Specialities') || [];

    return (Array.isArray(values) ? values : []).map((item: any) => ({
        organizationSpecialityId: valueOf(item, 'organizationSpecialityId', 'OrganizationSpecialityId'),
        specialityId: String(valueOf(item, 'specialityId', 'SpecialityId', 'id', 'Id') || ''),
        name: String(valueOf(item, 'name', 'Name') || ''),
        specialityStatus: valueOf(item, 'specialityStatus', 'SpecialityStatus'),
        isAssociated: Boolean(valueOf(item, 'isAssociated', 'IsAssociated')),
        triageSigns: Boolean(valueOf(item, 'triageSigns', 'TriageSigns')),
        status: String(valueOf(item, 'status', 'Status') || 'INACTIVO').toUpperCase(),
    })).filter((item: OrganizationSpecialityRow) => item.specialityId && item.name);
};

const OrganizationSpecialityManagement: React.FC<OrganizationSpecialityManagementProps> = ({
    organizationId,
    organizationName,
    maxSpecialities,
    getOrganizationManagementSpecialities: loadSpecialities,
    putOrganizationManagementSpeciality: updateSpeciality,
}) => {
    const [rows, setRows] = useState<OrganizationSpecialityRow[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingKey, setSavingKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await loadSpecialities?.(organizationId);
            setRows(normalizeRows(response));
        } catch (loadError) {
            setError(loadError instanceof Error ? loadError.message : 'No se pudo cargar las especialidades de la organizacion.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId]);

    const filteredRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return rows;
        return rows.filter(row =>
            row.name.toLowerCase().includes(query)
            || row.status.toLowerCase().includes(query)
            || (row.triageSigns ? 'triaje enfermeria' : 'consulta').includes(query)
        );
    }, [rows, search]);

    const activeCount = rows.filter(row => row.isAssociated && row.status === 'ACTIVO').length;
    const triageCount = rows.filter(row => row.isAssociated && row.status === 'ACTIVO' && row.triageSigns).length;

    const saveRow = async (row: OrganizationSpecialityRow, changes: Partial<OrganizationSpecialityRow>) => {
        const next = { ...row, ...changes };
        setSavingKey(row.specialityId);
        setError(null);
        try {
            const response = await updateSpeciality?.(organizationId, {
                specialityId: row.specialityId,
                triageSigns: next.triageSigns,
                status: next.status,
            });
            if (response) {
                await loadData();
            }
        } catch (saveError) {
            setError(saveError instanceof Error ? saveError.message : 'No se pudo actualizar la especialidad.');
        } finally {
            setSavingKey(null);
        }
    };

    const columns = [
        {
            title: 'Especialidad',
            key: 'name',
            render: (_: unknown, row: OrganizationSpecialityRow) => (
                <Space direction="vertical" size={0}>
                    <Text strong>{row.name}</Text>
                    <Text type="secondary">Catalogo: {row.specialityStatus || 'N/A'}</Text>
                </Space>
            ),
        },
        {
            title: 'Estado en la organizacion',
            key: 'status',
            width: 210,
            render: (_: unknown, row: OrganizationSpecialityRow) => (
                <Space>
                    <Switch
                        checked={row.isAssociated && row.status === 'ACTIVO'}
                        disabled={maxSpecialities != null
                            && activeCount >= maxSpecialities
                            && !(row.isAssociated && row.status === 'ACTIVO')}
                        loading={savingKey === row.specialityId}
                        checkedChildren={<CheckOutlined />}
                        unCheckedChildren={<CloseOutlined />}
                        onChange={(checked) => saveRow(row, {
                            isAssociated: checked,
                            status: checked ? 'ACTIVO' : 'INACTIVO',
                        })}
                    />
                    <Tag color={row.isAssociated && row.status === 'ACTIVO' ? 'green' : 'default'}>
                        {row.isAssociated && row.status === 'ACTIVO' ? 'ACTIVA' : 'INACTIVA'}
                    </Tag>
                </Space>
            ),
        },
        {
            title: 'Triaje',
            key: 'triageSigns',
            width: 190,
            render: (_: unknown, row: OrganizationSpecialityRow) => (
                <Space>
                    <Switch
                        checked={row.triageSigns}
                        disabled={!row.isAssociated || row.status !== 'ACTIVO'}
                        loading={savingKey === row.specialityId}
                        checkedChildren="Si"
                        unCheckedChildren="No"
                        onChange={(checked) => saveRow(row, { triageSigns: checked })}
                    />
                    <Tag color={row.triageSigns ? 'blue' : 'default'}>
                        {row.triageSigns ? 'ENFERMERIA' : 'CONSULTA'}
                    </Tag>
                </Space>
            ),
        },
    ];

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div>
                <Title level={3} style={{ marginBottom: 4 }}>Especialidades de la organizacion</Title>
                <Text type="secondary">Configura las especialidades disponibles y define si la atencion inicia en enfermeria o consulta.</Text>
            </div>

            {error && <Alert type="error" showIcon message="No se pudo actualizar la configuracion" description={error} closable onClose={() => setError(null)} />}

            <Card>
                <Space wrap style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space wrap>
                        <Tag color="blue">{activeCount}{maxSpecialities == null ? '' : ` / ${maxSpecialities}`} activas</Tag>
                        <Tag color="cyan">{triageCount} con triaje</Tag>
                    </Space>
                    <Space>
                        <Input
                            allowClear
                            prefix={<SearchOutlined />}
                            placeholder="Buscar especialidad"
                            value={search}
                            onChange={event => setSearch(event.target.value)}
                        />
                        <Button icon={<ReloadOutlined />} onClick={loadData}>Actualizar</Button>
                    </Space>
                </Space>
            </Card>
            {maxSpecialities != null && activeCount >= maxSpecialities && <Alert type="warning" showIcon message={`El plan permite máximo ${maxSpecialities} especialidades activas.`} />}

            <Card title={organizationName}>
                {loading ? <div style={{ textAlign: 'center', padding: 36 }}><Spin /></div> : filteredRows.length === 0 ? (
                    <Empty description="No hay especialidades disponibles" />
                ) : (
                    <Table rowKey="specialityId" columns={columns} dataSource={filteredRows} pagination={{ pageSize: 10 }} />
                )}
            </Card>
        </Space>
    );
};

export default connect(null, {
    getOrganizationManagementSpecialities,
    putOrganizationManagementSpeciality,
})(OrganizationSpecialityManagement);
